import { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Save, FolderOpen, Download, Undo, Redo, RotateCw, Maximize2, Minimize2, FilePlus, Loader2, FileJson, Image } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import Toolbar from './Toolbar';
import SaveDialog from './topbar/SaveDialog';
import LoadDialog from './topbar/LoadDialog';
import UserMenu from './topbar/UserMenu';
import ManualExportDialog from './topbar/ManualExportDialog';

interface TopbarProps {
  editorRef: React.MutableRefObject<any>;
  onSave: () => Promise<void>;
  onSaveAs: (name: string) => void;
  onLoad: (planId: string) => void;
  onExportPDF: () => Promise<void>;
  onExportImage: (format: string) => Promise<void>;
  onExportJSON?: () => Promise<void>;
  onUndo: () => void;
  onRedo: () => void;
  onReset: () => void;
  onLogin: () => void;
  hasUnsavedChanges?: boolean;
  currentFloorPlanName?: string;
}

const Topbar = ({
  editorRef,
  onSave,
  onSaveAs,
  onLoad,
  onExportPDF,
  onExportImage,
  onExportJSON,
  onUndo,
  onRedo,
  onReset,
  onLogin,
  hasUnsavedChanges,
  currentFloorPlanName
}: TopbarProps) => {
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showManualExportDialog, setShowManualExportDialog] = useState(false);
  const { toast } = useToast();
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsExporting(true);
      await onExportPDF();
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportImage = async (format: string) => {
    try {
      setIsExporting(true);
      await onExportImage(format);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    if (!onExportJSON) return;
    
    try {
      setIsExporting(true);
      await onExportJSON();
    } finally {
      setIsExporting(false);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
        toast({
          title: "Volledig scherm",
          description: "Je werkt nu in volledig scherm. Druk op ESC of de knop om terug te gaan.",
        });
      }).catch((err) => {
        toast({
          title: "Fout",
          description: "Kon niet naar volledig scherm gaan. " + err.message,
          variant: "destructive",
        });
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        toast({
          title: "Normaal scherm",
          description: "Je bent terug in normaal scherm.",
        });
      }).catch((err) => {
        toast({
          title: "Fout",
          description: "Kon volledig scherm niet verlaten. " + err.message,
          variant: "destructive",
        });
      });
    }
  };

  const handleJsonImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const json = JSON.parse(content);

        if (!json.nodes || !json.edges || !Array.isArray(json.nodes) || !Array.isArray(json.edges)) {
          toast({
            title: "Ongeldig bestand",
            description: "Het JSON-bestand heeft een ongeldig formaat",
            variant: "destructive",
          });
          return;
        }

        if (editorRef.current?.resetState) {
          editorRef.current.resetState(json.nodes, json.edges);
          toast({
            title: "Import succesvol",
            description: "Plattegrond is geïmporteerd vanuit JSON",
          });
        }
      } catch (error) {
        console.error("Error importing JSON:", error);
        toast({
          title: "Fout bij importeren",
          description: "Het JSON-bestand kon niet worden verwerkt",
          variant: "destructive",
        });
      }
    };

    reader.onerror = () => {
      toast({
        title: "Fout bij lezen",
        description: "Het bestand kon niet worden gelezen",
        variant: "destructive",
      });
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImageImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    console.log("Importeren afbeeldingen:", files.length, "bestanden");
    
    Array.from(files).forEach((file) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const imageUrl = e.target?.result as string;
          console.log("Afbeelding geladen, URL lengte:", imageUrl.length);
          
          if (editorRef.current) {
            const nodeId = `image-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            
            const position = {
              x: 100 + Math.random() * 200,
              y: 100 + Math.random() * 200
            };
            
            const newNode = {
              id: nodeId,
              type: 'equipment',
              position: position,
              data: {
                image: imageUrl,
                isImage: true,
                name: file.name.split('.')[0],
                width: 200,
                height: 200,
                rotation: 0,
                flipX: false,
                flipY: false
              },
              style: {
                width: 200,
                height: 200,
              }
            };
            
            console.log("Nieuwe node aangemaakt:", nodeId);
            
            const currentNodes = editorRef.current.getNodes() || [];
            
            if (editorRef.current.setNodes) {
              editorRef.current.setNodes([...currentNodes, newNode]);
              
              if (editorRef.current.saveToHistory) {
                editorRef.current.saveToHistory([...currentNodes, newNode], editorRef.current.getEdges() || []);
              }
              
              toast({
                title: "Afbeelding toegevoegd",
                description: `${file.name} is toegevoegd aan de plattegrond`,
              });
            } else {
              console.error("Editor referentie heeft geen setNodes functie");
              toast({
                title: "Fout bij importeren",
                description: `De afbeelding ${file.name} kon niet worden toegevoegd`,
                variant: "destructive",
              });
            }
          } else {
            console.error("Editor referentie is niet beschikbaar");
            toast({
              title: "Fout bij importeren",
              description: "Editor referentie is niet beschikbaar",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error("Fout bij importeren afbeelding:", error);
          toast({
            title: "Fout bij importeren",
            description: `De afbeelding ${file.name} kon niet worden verwerkt`,
            variant: "destructive",
          });
        }
      };

      reader.onerror = () => {
        toast({
          title: "Fout bij lezen",
          description: `Het bestand ${file.name} kon niet worden gelezen`,
          variant: "destructive",
        });
      };

      reader.readAsDataURL(file);
    });
    
    event.target.value = '';
  };

  const handleFloorPlanNameUpdate = (planId: string, newName: string) => {
    // This callback will be passed to LoadDialog to update the current name
    const urlParams = new URLSearchParams(window.location.search);
    const currentPlanId = urlParams.get('id');
    
    if (currentPlanId === planId) {
      // Update the name if this is the currently opened plan
      // This will be handled through the parent component
    }
  };

  return (
    <div className="border-b py-2 px-4 bg-white/50 backdrop-blur-sm">
      <div className="flex items-center justify-between max-w-screen-2xl mx-auto flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleSave} 
            disabled={isSaving}
            title="Opslaan" 
            aria-label="Opslaan"
            className="h-8 w-8"
          >
            {isSaving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShowSaveDialog(true)} 
            title="Opslaan als" 
            aria-label="Opslaan als"
            className="h-8 w-8"
          >
            <FilePlus className="h-5 w-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setShowLoadDialog(true)} 
            title="Openen" 
            className="h-8 w-8"
          >
            <FolderOpen className="h-5 w-5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                title="Exporteren" 
                className="h-8 w-8"
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Download className="h-5 w-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={handleExportPDF} disabled={isExporting}>
                Exporteer als PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportImage('png')} disabled={isExporting}>
                Exporteer als PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportImage('jpg')} disabled={isExporting}>
                Exporteer als JPG
              </DropdownMenuItem>
              {onExportJSON && (
                <DropdownMenuItem onClick={handleExportJSON} disabled={isExporting}>
                  Exporteer als JSON
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setShowManualExportDialog(true)}>
                Tip: handmatig exporteren
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <div className="h-5 w-px bg-border mx-2" />
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => jsonInputRef.current?.click()}
            title="Importeer JSON"
          >
            <FileJson className="h-5 w-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => imageInputRef.current?.click()}
            title="Importeer Afbeelding"
          >
            <Image className="h-5 w-5" />
          </Button>
          <div className="h-5 w-px bg-border mx-2" />
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onUndo} 
            title="Ongedaan maken"
            className="h-8 w-8"
          >
            <Undo className="h-5 w-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onRedo}
            title="Opnieuw"
            className="h-8 w-8"
          >
            <Redo className="h-5 w-5" />
          </Button>
          <div className="h-5 w-px bg-border mx-2" />
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onReset}
            title="Vernieuwen"
            className="h-8 w-8"
          >
            <RotateCw className="h-5 w-5" />
          </Button>
          <div className="h-5 w-px bg-border mx-2" />
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleFullscreen}
            title={isFullscreen ? "Verlaat volledig scherm" : "Volledig scherm"}
            className="h-8 w-8"
          >
            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </Button>
        </div>

        <div className="flex-grow flex justify-center items-center">
          <Link to="/dashboard" className="no-underline">
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight cursor-pointer hover:opacity-80 transition-opacity">
                <span className="text-gray-900">Turnles</span>
                <span className="text-[#1b9d3d]"> Planner</span>
              </h1>
              {currentFloorPlanName && (
                <div className="text-sm text-gray-600 mt-1">
                  {currentFloorPlanName}
                </div>
              )}
            </div>
          </Link>
        </div>

        <div className="flex items-center gap-4">
          <Toolbar editorRef={editorRef} />
          <UserMenu 
            onLogin={onLogin} 
            onSave={handleSave}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        </div>
      </div>
      
      <input
        ref={jsonInputRef}
        type="file"
        accept=".json"
        onChange={handleJsonImport}
        className="hidden"
      />
      <input
        ref={imageInputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        onChange={handleImageImport}
        multiple
        className="hidden"
      />
      
      <SaveDialog 
        open={showSaveDialog} 
        onOpenChange={setShowSaveDialog}
        onSave={onSaveAs}
      />
      
      <LoadDialog
        open={showLoadDialog}
        onOpenChange={setShowLoadDialog}
        onSelect={onLoad}
        onNameUpdate={handleFloorPlanNameUpdate}
      />
      
      <ManualExportDialog
        open={showManualExportDialog}
        onOpenChange={setShowManualExportDialog}
      />
    </div>
  );
};

export default Topbar;
